import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  FormControlLabel,
  CircularProgress
} from '@material-ui/core';
import Slider from '@material-ui/core/Slider';
import Checkbox from '@material-ui/core/Checkbox';
import axios from 'axios';
import { SERVER_NAME } from './config';
import { useAuth } from './auth/AuthContext';
import UnsubscribeDialog from './UnsubscribeDialog';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1300,
    margin: 0,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  optionsPopup: {
    width: '100%',
    padding: '0 15px 0 15px', // Remove bottom padding
    position: 'relative',
  },
  optionsTitle: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: '16px 24px',
    fontWeight: 500,
    borderBottom: '1px solid rgba(0,0,0,0.1)'
  },
  optionSection: {
    marginBottom: 20,
    padding: 15,
    borderRadius: 8,
    backgroundColor: '#f7f9fc',
    border: '1px solid #e0e7ff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
      backgroundColor: '#f0f4ff'
    }
  },
  optionControl: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    '&:last-child': {
      marginBottom: 0
    }
  },
  optionLabel: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    flex: '0 0 180px',
    marginRight: 15
  },
  optionValue: {
    flex: 1
  },
  sliderContainer: {
    flex: 1,
    '& .MuiSlider-root': {
      color: theme.palette.primary.main
    }
  },
  newsletterSection: {
    backgroundColor: '#e8f0fe',
    border: '1px solid #c9d8f6',
    padding: 20,
    borderRadius: 8,
    marginBottom: 24,
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
  },
  newsletterTitle: {
    fontWeight: 600,
    color: theme.palette.primary.dark,
    marginBottom: 10
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '8px 24px',
    backgroundColor: '#f5f5f5',
    marginTop: 0, // Remove the margin that was creating space
    borderTop: '1px solid #e0e0e0'
  },
  button: {
    margin: '0 8px',
    '&.MuiButton-containedPrimary': {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    }
  }
}));

export const OptionsPopup = ({ open, onClose, subscribeNewsletter: propSubscribeNewsletter, setSubscribeNewsletter: propSetSubscribeNewsletter, tourSpeedGraph, setTourSpeedGraph, tourSpeedList, setTourSpeedList, spinSpeed, setSpinSpeed, imageSize, setImageSize, useAspectRatio, setUseAspectRatio, resetCookieConsent, setResetCookieConsent }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { isAuthenticated, username, userID, subscribeNewsletter: contextSubscribeNewsletter, updateNewsletterSubscription } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  const [showUnsubscribeDialog, setShowUnsubscribeDialog] = useState(false);
  // Initialize with a default but we'll check multiple sources
  const [tempSubscribeNewsletter, setTempSubscribeNewsletter] = useState(false);
  const [tempTourSpeedGraph, setTempTourSpeedGraph] = useState(tourSpeedGraph);
  const [tempTourSpeedList, setTempTourSpeedList] = useState(tourSpeedList);
  const [tempSpinSpeed, setTempSpinSpeed] = useState(spinSpeed);
  const [tempImageSize, setTempImageSize] = useState(imageSize);
  const [tempUseAspectRatio, setTempUseAspectRatio] = useState(useAspectRatio);
  const [tempResetCookieConsent, setTempResetCookieConsent] = useState(resetCookieConsent);

  // Comprehensive check for newsletter subscription status from multiple sources
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      setIsLoading(true);
      console.log('OptionsPopup: Checking newsletter subscription status from all sources');
      
      // Check various sources
      const fromContext = !!contextSubscribeNewsletter;
      const fromProps = propSubscribeNewsletter === true || propSubscribeNewsletter === 'true';
      const fromLocalStorage = localStorage.getItem('subscribeNewsletter') === 'true';
      
      console.log('Newsletter subscription values:', {
        fromContext,
        fromProps,
        fromLocalStorage,
        contextValue: contextSubscribeNewsletter,
        propsValue: propSubscribeNewsletter,
        localStorageValue: localStorage.getItem('subscribeNewsletter')
      });
      
      // If authenticated, make a server check as well
      let fromServer = false;
      if (isAuthenticated && username) {
        try {
          // Check auth status from server to get latest subscription status
          const response = await axios.get(`${SERVER_NAME}/auth/status`, { withCredentials: true });
          console.log('Server subscription status check:', response.data);
          
          if (response.data && response.data.isAuthenticated) {
            fromServer = !!response.data.user?.subscribeNewsletter;
            
            // If server has definitive info, update context
            if (fromServer !== fromContext) {
              console.log('Updating context with server subscription status:', fromServer);
              updateNewsletterSubscription(fromServer);
            }
          }
        } catch (error) {
          console.error('Error checking subscription status from server:', error);
        }
      }
      
      // Use the most permissive value - if any source says subscribed, show as subscribed
      const isSubscribed = fromContext || fromProps || fromLocalStorage || fromServer;
      console.log('Final subscription status determination:', isSubscribed);
      
      // Update the checkbox state
      setTempSubscribeNewsletter(isSubscribed);
      setIsLoading(false);
    };
    
    // Only run when the dialog opens
    if (open) {
      checkSubscriptionStatus();
    }
  }, [contextSubscribeNewsletter, propSubscribeNewsletter, isAuthenticated, username, open, updateNewsletterSubscription]);
  
  // Debug rendered checkbox state
  useEffect(() => {
    console.log('Checkbox is now:', tempSubscribeNewsletter ? 'CHECKED' : 'UNCHECKED');
  }, [tempSubscribeNewsletter]);
  
  useEffect(() => {
    setTempTourSpeedGraph(tourSpeedGraph);
  }, [tourSpeedGraph]);
  
  useEffect(() => {
    setTempTourSpeedList(tourSpeedList);
  }, [tourSpeedList]);
  
  useEffect(() => {
    setTempSpinSpeed(spinSpeed);
  }, [spinSpeed]);
  
  useEffect(() => {
    setTempImageSize(imageSize);
  }, [imageSize]);
  
  useEffect(() => {
    setTempUseAspectRatio(useAspectRatio);
  }, [useAspectRatio]);
  
  useEffect(() => {
    setTempResetCookieConsent(resetCookieConsent);
  }, [resetCookieConsent]);

  const handleSliderChange = (event, newValue, setStateFunc) => {
    setStateFunc(newValue);
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    // Save UI preferences
    setTourSpeedGraph(tempTourSpeedGraph);
    setTourSpeedList(tempTourSpeedList);
    setSpinSpeed(tempSpinSpeed);
    setImageSize(tempImageSize);
    setUseAspectRatio(tempUseAspectRatio);
    setResetCookieConsent(tempResetCookieConsent);

    // Get the current value from AuthContext or props
    const currentValue = contextSubscribeNewsletter !== undefined ? 
      contextSubscribeNewsletter : propSubscribeNewsletter;
    
    // Check if subscription status has changed
    if (tempSubscribeNewsletter !== currentValue) {
      console.log(`Newsletter subscription changed from ${currentValue} to ${tempSubscribeNewsletter}`);
      
      // Update local state and localStorage
      updateNewsletterSubscription(tempSubscribeNewsletter);
      
      // Update props if provided
      if (propSetSubscribeNewsletter) {
        propSetSubscribeNewsletter(tempSubscribeNewsletter);
      }
      
      // If authenticated, also update on the server
      if (isAuthenticated && username) {
        try {
          console.log('Sending newsletter subscription update to server');
          const response = await axios.post(`${SERVER_NAME}/subscribeNewsletter`, {
            username,
            subscribeNewsletter: tempSubscribeNewsletter
          });
          
          console.log('Server response:', response.data);
          
          // Show success in console
          if (response.data?.success) {
            console.log('Newsletter subscription successfully updated on server');
          }
        } catch (error) {
          console.error('Error updating newsletter subscription on server:', error);
        }
      }
      
      // Close with update flag and new value
      onClose(true, tempSubscribeNewsletter);
    } else {
      // No changes to subscription status
      onClose(false);
    }
    
    setIsLoading(false);
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="options-dialog-title" maxWidth="sm" fullWidth>
      <DialogTitle id="options-dialog-title" className={classes.optionsTitle}>
        User Preferences
      </DialogTitle>
      <DialogContent className={classes.optionsPopup} style={{ paddingBottom: 0 }}>
        {/* Newsletter Subscription Section */}
        <div className={classes.newsletterSection}>
          <Typography variant="subtitle1" className={classes.newsletterTitle}>
            Newsletter Subscription
          </Typography>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <CircularProgress size={28} />
              <Typography variant="body2" style={{ marginLeft: 16, color: '#344767' }}>
                Checking subscription status...
              </Typography>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <div>
                <Typography variant="body2" style={{ color: '#344767', fontWeight: 500 }}>
                  {tempSubscribeNewsletter ? 
                    'You are currently subscribed to the E3D Daily Newsletter' : 
                    'You are not subscribed to the E3D Daily Newsletter'}
                </Typography>
                <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#64748B' }}>
                  {tempSubscribeNewsletter ? 
                    'Uncheck to stop receiving updates' : 
                    'Check to receive daily token insights and market trends'}
                </Typography>
              </div>
              <div>
                <Checkbox
                  checked={tempSubscribeNewsletter}
                  onChange={(e) => setTempSubscribeNewsletter(e.target.checked)}
                  inputProps={{ 'aria-label': 'newsletter subscription checkbox' }}
                  color="primary"
                  style={{ 
                    transform: 'scale(1.2)',
                    padding: '4px',
                    color: tempSubscribeNewsletter ? '#4fc3f7' : undefined
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Display Settings Section */}
        <div className={classes.optionSection}>
          <Typography variant="subtitle2" style={{ marginBottom: 15, fontWeight: 600, color: '#334155' }}>
            Display Settings
          </Typography>

          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              Graph Node Image Size
            </Typography>
            <div className={classes.sliderContainer}>
              <Slider
                value={tempImageSize}
                onChange={(e, newValue) => handleSliderChange(e, newValue, setTempImageSize)}
                aria-labelledby="image-size-slider"
                min={1}
                max={100}
              />
            </div>
          </div>

          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              Fill Image to Window
            </Typography>
            <div>
              <Checkbox
                checked={tempUseAspectRatio}
                onChange={(e) => setTempUseAspectRatio(e.target.checked)}
                inputProps={{ 'aria-label': 'aspect ratio checkbox' }}
                color="primary"
              />
            </div>
          </div>
        </div>

        {/* Animation Settings Section */}
        <div className={classes.optionSection}>
          <Typography variant="subtitle2" style={{ marginBottom: 15, fontWeight: 600, color: '#334155' }}>
            Animation Settings
          </Typography>

          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              Graph View Tour Delay (ms)
            </Typography>
            <div className={classes.sliderContainer}>
              <Slider
                value={tempTourSpeedGraph}
                onChange={(e, newValue) => handleSliderChange(e, newValue, setTempTourSpeedGraph)}
                aria-labelledby="tour-speed-graph-slider"
                min={1000}
                max={10000}
                marks={[{value: 1000, label: '1s'}, {value: 10000, label: '10s'}]}
              />
            </div>
          </div>

          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              List View Tour Delay (ms)
            </Typography>
            <div className={classes.sliderContainer}>
              <Slider
                value={tempTourSpeedList}
                onChange={(e, newValue) => handleSliderChange(e, newValue, setTempTourSpeedList)}
                aria-labelledby="tour-speed-list-slider"
                min={500}
                max={5000}
                marks={[{value: 500, label: '0.5s'}, {value: 5000, label: '5s'}]}
              />
            </div>
          </div>

          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              Spin Delay (ms)
            </Typography>
            <div className={classes.sliderContainer}>
              <Slider
                value={tempSpinSpeed}
                onChange={(e, newValue) => handleSliderChange(e, newValue, setTempSpinSpeed)}
                aria-labelledby="spin-speed-slider"
                min={1}
                max={1000}
                marks={[{value: 1, label: 'Fast'}, {value: 1000, label: 'Slow'}]}
              />
            </div>
          </div>
        </div>

        {/* Account Management Section */}
        {isAuthenticated && (
          <div className={classes.optionSection}>
            <Typography variant="subtitle2" style={{ marginBottom: 15, fontWeight: 600, color: '#334155' }}>
              Account Management
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Typography variant="body2" style={{ color: '#344767' }}>
                Username: <strong>{username}</strong>
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  if (typeof onClose === 'function') onClose(false);
                  navigate('/apikeys');
                }}
                style={{ alignSelf: 'flex-start' }}
              >
                API Keys
              </Button>
              <Button 
                variant="outlined" 
                color="secondary"
                onClick={() => {
                  console.log('Delete Account button clicked. Current auth state:', { 
                    userID, 
                    username, 
                    isAuthenticated 
                  });
                  setShowUnsubscribeDialog(true);
                }}
                style={{ alignSelf: 'flex-start', marginTop: '5px' }}
              >
                Delete Account
              </Button>
              <Typography variant="caption" style={{ color: '#64748B', marginTop: '5px' }}>
                Warning: Account deletion will permanently remove your data and cancel all subscriptions.
              </Typography>
            </div>
          </div>
        )}
        
        {/* Privacy Settings */}
        <div className={classes.optionSection} style={{ marginBottom: 0 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 15, fontWeight: 600, color: '#334155' }}>
            Privacy Settings
          </Typography>
          <div className={classes.optionControl}>
            <Typography variant="body2" className={classes.optionLabel}>
              Reset Cookie Consent
            </Typography>
            <div>
              <Checkbox
                checked={tempResetCookieConsent}
                onChange={(e) => setTempResetCookieConsent(e.target.checked)}
                inputProps={{ 'aria-label': 'reset cookie consent checkbox' }}
                color="primary"
              />
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button 
          onClick={handleCancel} 
          color="default" 
          className={classes.button}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary" 
          className={classes.button}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
      
      {showUnsubscribeDialog && (
        <UnsubscribeDialog
          open={showUnsubscribeDialog}
          onClose={() => setShowUnsubscribeDialog(false)}
          userID={userID} // Explicitly pass userID
          username={username} // Also pass username
        />
      )}
    </Dialog>
  );
};